import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ProgramSectionView from './ProgramSectionView';

const API = process.env.REACT_APP_API_URL || '';

// Loads the public program of an event (GET /api/public/events/{slug}/program and /people).
// status: 'loading', 'ready', or 'none' (unknown event, hidden event, unpublished program: the
// backend answers one 404 for all of them; a network error counts the same, so the public page
// never shows an error banner). A missing /people answer only drops the people grid. A program the
// admin published with only its PDF is ready too (P10a): its tree is empty and the section shows the
// download button alone.
export const usePublicProgram = (slug) => {
  const [state, setState] = useState({ status: 'loading', program: null, people: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', program: null, people: null });
    if (!slug) {
      setState({ status: 'none', program: null, people: null });
      return undefined;
    }
    const base = `${API}/api/public/events/${encodeURIComponent(slug)}`;
    Promise.all([
      axios.get(`${base}/program`).then((r) => r.data),
      axios.get(`${base}/people`).then((r) => r.data).catch(() => null),
    ])
      .then(([program, people]) => {
        if (cancelled) return;
        const hasEntries = Array.isArray(program?.entries) && program.entries.length > 0;
        const hasPdf = !!program?.programPdf?.url;
        const ready = hasEntries || hasPdf;
        setState({ status: ready ? 'ready' : 'none', program: ready ? program : null, people });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'none', program: null, people: null });
      });
    return () => { cancelled = true; };
  }, [slug]);

  return state;
};

// The program section of the public event page (decision 1 of plan §7.6). Renders nothing while
// loading and when there is no public program; onStatus tells the page which, so it can hide
// whatever it puts around the section. P15 places it in EventDetail instead of the PROGRAM button.
const PublicProgramSection = ({ slug, onStatus, showTitle }) => {
  const { status, program, people } = usePublicProgram(slug);

  useEffect(() => {
    if (onStatus) onStatus(status);
  }, [status, onStatus]);

  if (status !== 'ready') return null;
  return <ProgramSectionView program={program} people={people} showTitle={showTitle} />;
};

export default PublicProgramSection;
